import { CustomerLedgerHeaderCell, OrderHistoryMobileLabel } from './customer-activity-cells'
export { CustomerLedgerHeaderCell, OrderHistoryMobileLabel } from './customer-activity-cells'
export { ReviewsTab } from './CustomerReviewsTab'
import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import {
  CustomerInvoiceStatusPill,
  getCustomerInvoiceBalance,
} from '@/components/customers/customer-invoice-display'
import { DeliveryRouteDatePicker } from '@/components/orders/DeliveryRouteDatePicker'
import OrderPaymentStatusBadge from '@/components/orders/OrderPaymentStatusBadge'
import OrderRefundStatusBadge from '@/components/orders/OrderRefundStatusBadge'
import {
  isCancelledSalesOrder,
  shouldShowSalesOrderBalanceDue,
} from '@/components/orders/sales-order-presentation'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DetailCard,
  DetailIconBadge,
  DetailWell,
  detailDataHeaderClass,
  detailDataListClass,
  detailDataRowClass,
} from '@/components/patterns/detail-page'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { Textarea } from '@/components/ui/textarea'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can } from '@/lib/casl'
import dayjs from '@/lib/dayjs'
import { getLineItemDisplay } from '@/lib/line-item-display'
import {
  useCreateCustomerOccasionReminderMutation,
  useDeleteCustomerOccasionReminderMutation,
  useUpdateCustomerInternalNotesMutation,
  useUpdateCustomerOccasionReminderMutation,
} from '@/lib/services/customers/customers.query'
import { customerOccasionReminderSchema } from '@/lib/services/customers/customers.schema'
import { getDuplicateSalesOrderCartSearchParams } from '@/lib/services/sales-orders/sales-order.schema'
import { useDuplicateSalesOrderToCartMutation } from '@/lib/services/sales-orders/sales-orders.query'
import { cn, formatCurrency, getErrorMessage, normalizeCalendarDateInput } from '@/lib/utils'
import { Link, useNavigate } from '@tanstack/react-router'
import { CalendarHeart, Copy, NotebookPen, Pencil, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

const ORDER_HISTORY_PAGE_SIZE = 10
const scheduledOccasionDateLabels = {
  valentines_day: 'February 14',
  mothers_day: 'Second Sunday in May',
}

function occasionRequiresDate(occasionType) {
  return !scheduledOccasionDateLabels[occasionType]
}

function occasionRequiresLabel(occasionType) {
  return occasionType === 'other'
}

function occasionUsesHonoree(occasionType) {
  return occasionType === 'birthday'
}

function updateReminderDraftOccasionType(draft, occasionType) {
  return {
    ...draft,
    occasion_type: occasionType,
    occasion_on:
      !occasionRequiresDate(draft.occasion_type) && occasionRequiresDate(occasionType)
        ? ''
        : draft.occasion_on,
  }
}

function emptyReminderDraft() {
  return {
    occasion_type: 'birthday',
    occasion_label: '',
    honoree_name: '',
    occasion_on: dayjs().format('YYYY-MM-DD'),
  }
}

function getReminderLabel(reminder) {
  if (!reminder) {
    return ''
  }

  if (reminder.display_label) {
    return reminder.display_label
  }

  return reminder.occasion_type === 'other'
    ? reminder.occasion_label || 'Special Occasion'
    : reminder.occasion_type
}

function formatReminderDetail(value, fallback = '-') {
  const text = String(value ?? '').trim()

  return text || fallback
}

function formatReminderWhat(reminder) {
  const label = getReminderLabel(reminder) || 'Occasion'

  return label
    .split(' ')
    .map((word) => (word ? `${word[0].toUpperCase()}${word.slice(1)}` : word))
    .join(' ')
}

function formatReminderWhen(reminder) {
  if (reminder?.next_occurrence_on) {
    return dayjs(reminder.next_occurrence_on).format('MMM D, YYYY')
  }

  return reminder?.occasion_month && reminder?.occasion_day
    ? `${reminder.occasion_month}/${reminder.occasion_day}`
    : '-'
}

function getReminderDraft(reminder) {
  if (!reminder) {
    return emptyReminderDraft()
  }

  return {
    occasion_type: reminder.occasion_type || 'birthday',
    occasion_label: reminder.occasion_label || '',
    honoree_name: reminder.honoree_name || '',
    occasion_on: reminder.editor_occasion_on || dayjs().format('YYYY-MM-DD'),
  }
}

export function InternalNotesPanel({ customer, embedded = false }) {
  const updateCustomerInternalNotesMutation = useUpdateCustomerInternalNotesMutation()
  const [draft, setDraft] = useState('')
  const [editingIndex, setEditingIndex] = useState(null)
  const notes = Array.isArray(customer.internal_notes) ? customer.internal_notes : []

  const resetDraft = () => {
    setDraft('')
    setEditingIndex(null)
  }

  const persistNotes = async (nextNotes, { resetAfterSuccess = true } = {}) => {
    try {
      await updateCustomerInternalNotesMutation.mutateAsync({
        customerId: customer.id,
        internal_notes: nextNotes,
      })
      toast.success('Notes updated')
      if (resetAfterSuccess) {
        resetDraft()
      }
    } catch (error) {
      toast.error('Failed to update notes', {
        description: getErrorMessage(error),
      })
    }
  }

  const saveNote = () => {
    const note = draft.trim()
    if (!note) {
      return
    }

    let nextNotes = [...notes]
    if (editingIndex === null) {
      nextNotes = [{ note, timestamp: new Date().toISOString() }, ...notes]
    } else {
      nextNotes[editingIndex] = {
        ...nextNotes[editingIndex],
        note,
      }
    }

    void persistNotes(nextNotes)
  }

  const deleteNote = (index) => {
    const shouldResetDraft = editingIndex === index
    const nextNotes = notes.filter((_, noteIndex) => noteIndex !== index)

    if (!shouldResetDraft && editingIndex !== null && index < editingIndex) {
      setEditingIndex(editingIndex - 1)
    }

    void persistNotes(nextNotes, {
      resetAfterSuccess: shouldResetDraft,
    })
  }

  const content = (
    <Can do="edit" on="customers" field="notes" passThrough>
      {(canEditNotes) => (
        <>
          {!embedded ? (
            <div className="flex items-start gap-3">
              <DetailIconBadge icon={NotebookPen} tone="periwinkle" />
              <div className="min-w-0">
                <Typography variant="eyebrow">Internal Notes</Typography>
                <Typography variant="supporting" className="mt-1 block">
                  Private notes for your shop only.
                </Typography>
              </div>
            </div>
          ) : null}
          {canEditNotes ? (
            <>
              <Textarea
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder="Private shop note..."
                rows={3}
              />
              <div className="flex justify-end gap-2">
                {editingIndex !== null ? (
                  <Button variant="outline" size="sm" onClick={resetDraft}>
                    Cancel
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  onClick={saveNote}
                  disabled={!draft.trim() || updateCustomerInternalNotesMutation.isPending}
                >
                  <Plus className="size-4" />
                  {editingIndex === null ? 'Add Note' : 'Save Note'}
                </Button>
              </div>
            </>
          ) : null}
          <div className="space-y-3">
            {notes.length === 0 ? (
              <Typography variant="supporting">No internal notes yet.</Typography>
            ) : (
              notes.map((note, index) => (
                <DetailWell key={`${note.timestamp}-${index}`} tone="periwinkle">
                  <p className="whitespace-pre-wrap text-base/6">{note.note}</p>
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <Typography variant="supporting">
                      {note.timestamp ? dayjs(note.timestamp).format('MMM D, YYYY h:mm A') : ''}
                    </Typography>
                    {canEditNotes ? (
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          aria-label="Edit note"
                          onClick={() => {
                            setDraft(note.note)
                            setEditingIndex(index)
                          }}
                        >
                          <Pencil className="size-4" />
                        </Button>
                        <ConfirmationDialog
                          variant="destructive"
                          title="Delete note"
                          description="Are you sure you want to delete this internal note?"
                          confirmText="Delete"
                          pendingText="Deleting..."
                          isPending={updateCustomerInternalNotesMutation.isPending}
                          onConfirm={() => deleteNote(index)}
                          trigger={
                            <Button
                              variant="ghostDestructive"
                              size="icon-xs"
                              aria-label="Delete note"
                            >
                              <Trash2 className="size-4" />
                            </Button>
                          }
                        />
                      </div>
                    ) : null}
                  </div>
                </DetailWell>
              ))
            )}
          </div>
        </>
      )}
    </Can>
  )

  if (embedded) {
    return <div className="space-y-4 p-5">{content}</div>
  }

  return (
    <DetailCard accent="personalTouches" contentClassName="space-y-4 p-5">
      {content}
    </DetailCard>
  )
}

function formatOrderHistoryItems(order) {
  const items = Array.isArray(order?.sales_order_items) ? order.sales_order_items : []
  const names = items
    .map((item) => getLineItemDisplay(item).primaryName)
    .filter((name) => name && name !== 'Item')
  const itemCount =
    Number(order?.item_count ?? 0) ||
    items.reduce((total, item) => total + Math.max(1, Number(item.quantity ?? 1)), 0)

  if (names.length === 0) {
    return itemCount > 0 ? `${itemCount} item${itemCount === 1 ? '' : 's'}` : 'No item details'
  }

  if (names.length === 1) {
    return names[0]
  }

  return `${names[0]} + ${names.length - 1} more`
}

function OrderHistoryStatus({ order, hasBalanceDue, daysPastDue }) {
  // One status per row, so a cancelled order says Cancelled even after a refund.
  if (isCancelledSalesOrder(order)) {
    return (
      <OrderPaymentStatusBadge
        status={order.payment_status}
        cancelledAt={order.cancelled_at}
        size="sm"
      />
    )
  }

  if (order.refund_status) {
    return <OrderRefundStatusBadge status={order.refund_status} size="sm" />
  }

  if (hasBalanceDue) {
    return <CustomerInvoiceStatusPill invoice={{ ...order, days_past_due: daysPastDue }} />
  }

  return <OrderPaymentStatusBadge status={order.payment_status} size="sm" />
}

export function OrderHistoryTab({ orders }) {
  const path = usePathHelper()
  const navigate = useNavigate()
  const duplicateMutation = useDuplicateSalesOrderToCartMutation()
  const [orderHistoryPage, setOrderHistoryPage] = useState(1)
  const totalPages = Math.max(Math.ceil(orders.length / ORDER_HISTORY_PAGE_SIZE), 1)
  const currentPage = Math.min(orderHistoryPage, totalPages)
  const startIndex = (currentPage - 1) * ORDER_HISTORY_PAGE_SIZE
  const visibleOrders = orders.slice(startIndex, startIndex + ORDER_HISTORY_PAGE_SIZE)

  const duplicateOrder = async (order) => {
    try {
      const orderId = order?.id
      const response = await duplicateMutation.mutateAsync(orderId)
      await navigate({
        ...path.salesOrders
          .create()
          .search(getDuplicateSalesOrderCartSearchParams(response, order?.fulfillment_type)).link,
      })
    } catch (error) {
      toast.error('Failed to duplicate order', {
        description: getErrorMessage(error),
      })
    }
  }

  if (orders.length === 0) {
    return (
      <div className="p-5">
        <Typography variant="supporting">No orders yet.</Typography>
      </div>
    )
  }

  return (
    <div>
      <div
        className={cn(
          'hidden grid-cols-[7.5rem_minmax(8rem,0.75fr)_minmax(10rem,1fr)_7rem_7.5rem] gap-4 px-4 py-2.5 md:grid',
          detailDataHeaderClass
        )}
      >
        <CustomerLedgerHeaderCell>Order</CustomerLedgerHeaderCell>
        <CustomerLedgerHeaderCell>For</CustomerLedgerHeaderCell>
        <CustomerLedgerHeaderCell>Items</CustomerLedgerHeaderCell>
        <CustomerLedgerHeaderCell className="pr-3 text-right">Total</CustomerLedgerHeaderCell>
        <CustomerLedgerHeaderCell className="text-right">Action</CustomerLedgerHeaderCell>
      </div>
      <div className={detailDataListClass}>
        {visibleOrders.map((order) => {
          const remainingTotal = getCustomerInvoiceBalance(order)
          // Owed while the server can still collect (more than 1¢, collection open), as the order list reads it.
          const hasBalanceDue = shouldShowSalesOrderBalanceDue(order)
          const daysPastDue =
            hasBalanceDue && order.due_date
              ? Math.max(
                  0,
                  dayjs()
                    .startOf('day')
                    .diff(dayjs(normalizeCalendarDateInput(order.due_date)), 'day')
                )
              : 0
          const orderItemsSummary = formatOrderHistoryItems(order)

          return (
            <div
              key={order.id}
              className={cn(
                'grid gap-y-3 px-4 py-3 text-sm md:grid-cols-[7.5rem_minmax(8rem,0.75fr)_minmax(10rem,1fr)_7rem_7.5rem] md:items-center md:gap-x-4',
                detailDataRowClass
              )}
            >
              <div className="min-w-0">
                <OrderHistoryMobileLabel>Order</OrderHistoryMobileLabel>
                <Link {...path.salesOrders.show(order.id).link}>
                  <Typography variant="labelStrong" interaction="hoverUnderline">
                    Order #{order.id}
                  </Typography>
                </Link>
                <Typography variant="supporting" className="mt-1 block">
                  {order.created_at
                    ? `Placed ${dayjs(order.created_at).format('MMM D, YYYY')}`
                    : ''}
                </Typography>
              </div>

              <div className="min-w-0">
                <OrderHistoryMobileLabel>For</OrderHistoryMobileLabel>
                <span className="block truncate font-medium">
                  {order.recipient_name || order.ordering_contact || 'No order contact'}
                </span>
                <div className="mt-1 flex md:hidden">
                  <OrderHistoryStatus
                    order={order}
                    hasBalanceDue={hasBalanceDue}
                    daysPastDue={daysPastDue}
                  />
                </div>
              </div>

              <div className="min-w-0">
                <OrderHistoryMobileLabel>Items</OrderHistoryMobileLabel>
                <span className="block truncate text-muted-foreground">{orderItemsSummary}</span>
                <span className="mt-1 hidden md:block">
                  <OrderHistoryStatus
                    order={order}
                    hasBalanceDue={hasBalanceDue}
                    daysPastDue={daysPastDue}
                  />
                </span>
              </div>

              <div className="min-w-0 md:pr-3 md:text-right">
                <OrderHistoryMobileLabel>Total</OrderHistoryMobileLabel>
                <span className="font-semibold tabular-nums">
                  {/* Staff who see receivables get list rows (`financial_state`); others the profile rows (`order_total`). */}
                  {formatCurrency(order.financial_state?.order_total ?? order.order_total)}
                </span>
                {hasBalanceDue ? (
                  <Typography variant="label" tone="warning" className="mt-1 block">
                    {formatCurrency(remainingTotal)} due
                  </Typography>
                ) : null}
              </div>

              <div className="flex md:justify-end">
                <Can do="view" on="sales-orders">
                  <Can do="create" on="sales-orders">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={duplicateMutation.isPending}
                      onClick={() => duplicateOrder(order)}
                    >
                      <Copy className="size-4" />
                      Copy Order
                    </Button>
                  </Can>
                </Can>
              </div>
            </div>
          )
        })}
      </div>
      {orders.length > ORDER_HISTORY_PAGE_SIZE ? (
        <div className="flex flex-col gap-3 border-t border-border/40 bg-elevated/30 px-4 py-3 sm:flex-row sm:items-center sm:justify-between dark:bg-elevated/20">
          <Typography variant="label" tone="muted">
            Showing {startIndex + 1}-{Math.min(startIndex + ORDER_HISTORY_PAGE_SIZE, orders.length)}{' '}
            of {orders.length} loaded orders
          </Typography>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => setOrderHistoryPage(Math.max(currentPage - 1, 1))}
            >
              Previous
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={currentPage >= totalPages}
              onClick={() => setOrderHistoryPage(Math.min(currentPage + 1, totalPages))}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  )
}

export function MessagesTab({ messages }) {
  const path = usePathHelper()

  if (messages.length === 0) {
    return (
      <div className="p-5">
        <Typography variant="supporting">No linked messages yet.</Typography>
      </div>
    )
  }

  return (
    <div className={detailDataListClass}>
      {messages.map((message) => (
        <div
          key={message.id}
          className={cn(
            'flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between',
            detailDataRowClass
          )}
        >
          <div className="min-w-0 space-y-1">
            <Link {...path.messages.show(message.id).link}>
              <Typography variant="labelStrong" interaction="hoverUnderline">
                {message.name || `Message #${message.id}`}
              </Typography>
            </Link>
            <Typography variant="supporting" className="block">
              {message.created_at ? dayjs(message.created_at).format('MMM D, YYYY h:mm A') : ''}
            </Typography>
            <p className="line-clamp-2 text-base/6">{message.message}</p>
          </div>
          <Badge variant={message.is_read ? 'muted' : 'brand'}>
            {message.is_read ? 'Read' : 'Unread'}
          </Badge>
        </div>
      ))}
    </div>
  )
}

export function CustomerTabContentCount({ count, label }) {
  const displayCount = Math.max(Number(count ?? 0), 0)

  if (displayCount === 0) {
    return null
  }

  return (
    <span
      data-testid={`${label}-tab-content-count`}
      className="ml-0.5 text-xs font-medium text-muted-foreground"
    >
      ({displayCount})
    </span>
  )
}

function ReminderDialog({ draft, open, onChange, onOpenChange, onSubmit, isSaving, mode }) {
  const showHonoree = occasionUsesHonoree(draft.occasion_type)
  const showLabel = occasionRequiresLabel(draft.occasion_type)
  const showDate = occasionRequiresDate(draft.occasion_type)
  const scheduledDateLabel = scheduledOccasionDateLabels[draft.occasion_type] || ''

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{mode === 'edit' ? 'Edit Occasion' : 'Add Occasion'}</DialogTitle>
          <DialogDescription>Customer occasion reminders</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div
            data-testid="occasion-type-row"
            className={cn('grid gap-3', showLabel && 'sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]')}
          >
            <div className="grid gap-2">
              <Label>Occasion</Label>
              <Select
                value={draft.occasion_type}
                onValueChange={(value) => onChange(updateReminderDraftOccasionType(draft, value))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="birthday">Birthday</SelectItem>
                  <SelectItem value="anniversary">Anniversary</SelectItem>
                  <SelectItem value="valentines_day">Valentine&apos;s Day</SelectItem>
                  <SelectItem value="mothers_day">Mother&apos;s Day</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {showLabel ? (
              <div className="grid gap-2">
                <Label>Occasion Name</Label>
                <Input
                  value={draft.occasion_label}
                  onChange={(event) => onChange({ ...draft, occasion_label: event.target.value })}
                />
              </div>
            ) : null}
          </div>
          {showHonoree ? (
            <div className="grid gap-2">
              <Label>Recipient</Label>
              <Input
                value={draft.honoree_name}
                onChange={(event) => onChange({ ...draft, honoree_name: event.target.value })}
              />
            </div>
          ) : null}
          {showDate ? (
            <div className="grid gap-2">
              <Label>Date</Label>
              <DeliveryRouteDatePicker
                value={draft.occasion_on}
                onChange={(value) => onChange({ ...draft, occasion_on: value })}
                className="h-9"
              />
            </div>
          ) : (
            <div className="grid gap-2">
              <Label htmlFor="occasion-fixed-date">Date</Label>
              <Input disabled id="occasion-fixed-date" readOnly value={scheduledDateLabel} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={isSaving}>
            {mode === 'edit' ? 'Save Occasion' : 'Add Occasion'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function OccasionReminderFact({ label, children }) {
  return (
    <div className="min-w-0">
      <Typography variant="eyebrow" className="block">
        {label}
      </Typography>
      <span className="mt-1 block truncate text-base/6 font-semibold text-foreground">
        {children}
      </span>
    </div>
  )
}

export function OccasionRemindersPanel({ customer, defaultCreateOpen = false }) {
  const reminders = customer.occasion_reminders || []
  const createReminder = useCreateCustomerOccasionReminderMutation()
  const updateReminder = useUpdateCustomerOccasionReminderMutation()
  const deleteReminder = useDeleteCustomerOccasionReminderMutation()
  const [dialogState, setDialogState] = useState(() => ({
    open: defaultCreateOpen,
    mode: 'create',
    reminder: null,
  }))
  const [draft, setDraft] = useState(emptyReminderDraft)
  const isSaving = createReminder.isPending || updateReminder.isPending

  const openCreateDialog = () => {
    setDraft(emptyReminderDraft())
    setDialogState({ open: true, mode: 'create', reminder: null })
  }

  const openEditDialog = (reminder) => {
    setDraft(getReminderDraft(reminder))
    setDialogState({ open: true, mode: 'edit', reminder })
  }

  const closeDialog = () => {
    setDialogState((current) => ({ ...current, open: false }))
  }

  const saveReminder = async () => {
    const payload = {
      occasion_type: draft.occasion_type,
      occasion_label: occasionRequiresLabel(draft.occasion_type) ? draft.occasion_label : null,
      honoree_name: occasionUsesHonoree(draft.occasion_type) ? draft.honoree_name : null,
      occasion_on: occasionRequiresDate(draft.occasion_type) ? draft.occasion_on : null,
    }
    const parsedPayload = customerOccasionReminderSchema.safeParse(payload)

    if (!parsedPayload.success) {
      toast.error('Failed to save occasion', {
        description: getErrorMessage(parsedPayload.error.issues),
      })
      return
    }

    try {
      if (dialogState.mode === 'edit') {
        await updateReminder.mutateAsync({
          customerId: customer.id,
          reminderId: dialogState.reminder.id,
          ...parsedPayload.data,
        })
        toast.success('Occasion updated')
      } else {
        await createReminder.mutateAsync({
          customerId: customer.id,
          ...parsedPayload.data,
        })
        toast.success('Occasion added')
      }
      closeDialog()
    } catch (error) {
      toast.error('Failed to save occasion', {
        description: getErrorMessage(error),
      })
    }
  }

  const removeReminder = async (reminder) => {
    try {
      await deleteReminder.mutateAsync({
        customerId: customer.id,
        reminderId: reminder.id,
      })
      toast.success('Occasion removed')
    } catch (error) {
      toast.error('Failed to remove occasion', {
        description: getErrorMessage(error),
      })
    }
  }

  return (
    <div>
      <div
        className={cn('flex items-center justify-between gap-3 px-4 py-3', detailDataHeaderClass)}
      >
        <Typography variant="h5" className="pb-0">
          Occasion Reminders
        </Typography>
        <Can do="edit" on="customers">
          <Button size="sm" variant="outline" onClick={openCreateDialog}>
            <Plus className="size-4" />
            Add Occasion
          </Button>
        </Can>
      </div>
      {reminders.length === 0 ? (
        <div className="p-5">
          <Typography variant="supporting">No occasion reminders yet.</Typography>
        </div>
      ) : (
        <div className="grid gap-3 p-4 md:grid-cols-2">
          {reminders.map((reminder) => (
            <DetailWell key={reminder.id} tone="amethyst">
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 flex-1 gap-3">
                  <DetailIconBadge
                    icon={CalendarHeart}
                    tone="amethyst"
                    className="size-9 rounded-xl"
                    iconClassName="size-4"
                  />
                  <div className="min-w-0 flex-1 pt-0.5">
                    <div className="grid gap-x-6 gap-y-3 sm:grid-cols-3">
                      <OccasionReminderFact label="What">
                        {formatReminderWhat(reminder)}
                      </OccasionReminderFact>
                      <OccasionReminderFact label="Who">
                        {formatReminderDetail(reminder.honoree_name)}
                      </OccasionReminderFact>
                      <OccasionReminderFact label="When">
                        {formatReminderWhen(reminder)}
                      </OccasionReminderFact>
                    </div>
                  </div>
                </div>
                <Can do="edit" on="customers">
                  <div className="flex shrink-0 items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Edit occasion"
                      onClick={() => openEditDialog(reminder)}
                    >
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Delete occasion"
                      disabled={deleteReminder.isPending}
                      onClick={() => removeReminder(reminder)}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </Can>
              </div>
            </DetailWell>
          ))}
        </div>
      )}
      <ReminderDialog
        draft={draft}
        open={dialogState.open}
        onChange={setDraft}
        onOpenChange={(open) => {
          if (!open) closeDialog()
        }}
        onSubmit={saveReminder}
        isSaving={isSaving}
        mode={dialogState.mode}
      />
    </div>
  )
}
